import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, Link, useParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { JobLockManager } from '../../components/common/JobLockManager';
import { MoveBackToProductionButton } from '../../components/jobs/MoveBackToProductionButton';
import { PAGE_COLORS } from '../../utils/pageColors';
import { useGetJobQuery, usePatchJobMutation, useAssignDeliveryMutation, useAddJobNoteMutation, useDeleteJobMutation, useGetUserProfilesQuery, useGetDeliveryStatusQuery, useCompleteProductionMutation, useRemakeJobMutation } from '../../store/slices/apiSlice';
import type { JobFormData, JobStatus, JobPriority, JobFulfillment, JobSource, UserProfile, JobEditPageProps, JobPalletType } from '../../types';
import {
  buildDeliveryAssignOptions,
  formatGlassWeightKgForInput,
  formatGlassWeightKgPreviewKg,
  isPalletDeliverySelection,
  PALLET_TYPE_OPTIONS,
  parseGlassWeightKgInput,
} from '../../utils/palletJobFields';
import { ClipboardList, ArrowLeft, Trash2, Copy } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { OrdersEditPanel } from './OrdersEditPanel';
import { DeliveryWorkflow } from '../delivery-jobs/components/DeliveryWorkflow';
import { CollectionTab } from '../../components/jobs/CollectionTab';
import { CompleteJobConfirmDialog } from '../../components/production-line/CompleteJobConfirmDialog';
import { SkipProductionDateFields } from '../../components/common/SkipProductionDateFields';
import { formatDateForInput, validateAllJobDates } from '../../utils/jobDateValidation';
import { findLatestJobEditSkipCompletion } from '../../utils/jobProductionHistory';
import { formatJobDateLabel } from '../../utils/jobDateDisplay';
import { JobProductionDatesCard } from '../../components/jobs/JobProductionDatesCard';
import { DeliveryDetailsForm } from '../../components/delivery-management/DeliveryDetailsForm';
import {
  getJobStatusBadge,
  getAdminJobStatusLabel,
  getAdminJobStatusPillClassNames,
} from '../../utils/jobStatusMapper';

function skipDateFieldChanged(jobVal: string | null | undefined, modalVal: string): boolean {
  return formatDateForInput(modalVal || null) !== formatDateForInput(jobVal || null);
}

const schema = z.object({
  source_type: z.enum(['manual', 'website']),
  total_items: z.coerce.number().int().nonnegative(),
  customer_first_name: z.string().min(1, 'First name is required'),
  customer_last_name: z.string().min(1, 'Last name is required'),
  customer_email: z.string().max(160).optional().or(z.literal('')).transform(v => v || null),
  customer_mobile_number: z.string().optional().or(z.literal('')).transform(v => v || null),
  delivery_address_line1: z.string().min(1, 'Delivery address line 1 is required'),
  delivery_address_line2: z.string().optional().or(z.literal('')).transform(v => v || null),
  delivery_city: z.string().optional().or(z.literal('')).transform(v => v || null),
  delivery_postcode: z.string().min(1, 'Delivery postcode is required'),
  delivery_notes: z.string().optional().or(z.literal('')).transform(v => v || null),
  invoice_address_line1: z.string().min(1, 'Invoice address line 1 is required'),
  invoice_address_line2: z.string().optional().or(z.literal('')).transform(v => v || null),
  invoice_city: z.string().optional().or(z.literal('')).transform(v => v || null),
  invoice_postcode: z.string().min(1, 'Invoice postcode is required'),
  delivery_user: z.any().optional(),
  total_amount: z.union([z.string(), z.number()]).transform(v => {
    const num = typeof v === 'string' ? parseFloat(v) : v;
    if (Number.isNaN(num)) throw new Error('Invalid amount');
    return Number(num.toFixed(2));
  }),
  status: z.enum(['new', 'in_progress', 'on_hold', 'completed', 'cancelled', 'production']),
  priority: z.enum(['low', 'normal', 'high', 'urgent']),
  fulfillment_type: z.enum(['delivery', 'collection', 'pallet']),
  job_reference: z.string().optional().or(z.literal('')).transform(v => v || null),
  website_job_number: z.string().optional().or(z.literal('')).transform(v => v || null),
  website_order_created_at: z.string().optional().or(z.literal('')).transform(v => v || null),
  admin_notes: z.string().optional().or(z.literal('')).transform(v => v || null),
  pallet_type: z.enum(['no_preset', 'micro', 'half', 'half_plus', 'full', 'oversize_full']),
  glass_weight_kg: z.string().optional(),
  small_van: z.boolean(),
});

export const JobEditPage: React.FC<JobEditPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const { data: job, isLoading, isError, refetch } = useGetJobQuery(id || '', { skip: !id });
  const [patchJob, { isLoading: isSaving }] = usePatchJobMutation();
  const [assignDelivery, { isLoading: isAssigning }] = useAssignDeliveryMutation();
  const [addNote, { isLoading: isAddingNote }] = useAddJobNoteMutation();
  const [deleteJob, { isLoading: isDeleting }] = useDeleteJobMutation();
  const [completeProduction, { isLoading: isCompletingProduction }] = useCompleteProductionMutation();
  const [remakeJob, { isLoading: isRemaking }] = useRemakeJobMutation();

  const { data: userProfiles } = useGetUserProfilesQuery({ page: 1, page_size: 100, ordering: 'user__username' });
  const { data: deliveryData, refetch: refetchDelivery } = useGetDeliveryStatusQuery(id || '', { skip: !id });

  const [copyInvoiceToDelivery, setCopyInvoiceToDelivery] = useState(false);
  const [selectedDeliveryUser, setSelectedDeliveryUser] = useState<string>('');
  const [quickStatus, setQuickStatus] = useState<JobStatus>('in_progress');
  const [quickPriority, setQuickPriority] = useState<JobPriority>('normal');
  const [quickFulfillment, setQuickFulfillment] = useState<JobFulfillment>('delivery');
  const [quickNote, setQuickNote] = useState('');
  const [notesOverride, setNotesOverride] = useState<string | null>(null);
  const [showSkipProductionDialog, setShowSkipProductionDialog] = useState(false);
  const [showRemakeModal, setShowRemakeModal] = useState(false);
  const [skipModalDates, setSkipModalDates] = useState({
    glass_arrival_date: '',
    manufacture_date: '',
    potential_delivery_date: '',
  });
  const [isSkipDatePatching, setIsSkipDatePatching] = useState(false);
  const prevSkipDialogOpen = useRef(false);

  const deliveryOptions = useMemo(
    () => buildDeliveryAssignOptions(userProfiles),
    [userProfiles]
  );

  const isPalletAssign = useMemo(
    () =>
      isPalletDeliverySelection(
        selectedDeliveryUser ||
          (job?.delivery_user != null ? String(job.delivery_user) : ''),
        deliveryOptions,
        job?.delivery_user_username
      ),
    [selectedDeliveryUser, job?.delivery_user, job?.delivery_user_username, deliveryOptions]
  );

  const assignedDeliveryUserRole = useMemo(() => {
    if (!job?.delivery_user || !userProfiles?.results) return null;
    const assignedUser = userProfiles.results.find((p: UserProfile) => p.user === job.delivery_user);
    return assignedUser?.role_details?.name || null;
  }, [job?.delivery_user, userProfiles]);

  const jobEditSkipCompletion = useMemo(
    () => findLatestJobEditSkipCompletion(job?.metadata as Record<string, unknown> | undefined),
    [job?.metadata]
  );

  /** Backend uses production_complete; frontend ProductionStatus union omits it for workflow tabs. */
  const isJobProductionComplete =
    job?.status === 'completed' &&
    (job?.production_status as string | null | undefined) === 'production_complete';

  const parsedNotes = useMemo(() => {
    const list = (job?.admin_notes_list && job.admin_notes_list.length > 0) ? job.admin_notes_list : null;
    let items = [] as Array<{ timestamp: string | null; author: string | null; text: string }>;
    if (list) {
      items = list.map(n => ({ timestamp: n.timestamp || null, author: n.username || null, text: n.text }));
    } else {
      const source = notesOverride ?? job?.admin_notes ?? '';
      const lines = source.split('\n').filter(l => l.trim() !== '');
      items = lines.map(lineRaw => {
        const line = lineRaw.trim();
        const match = line.match(/^\[(.*?)\]\s+([^:]+):\s*(.*)$/);
        if (match) {
          return { timestamp: match[1], author: match[2], text: match[3] };
        }
        return { timestamp: null, author: null, text: line };
      });
    }
    // Sort: newest first by timestamp; items without timestamp go to bottom preserving relative order
    const withTs = items.filter(i => i.timestamp);
    const withoutTs = items.filter(i => !i.timestamp);
    withTs.sort((a, b) => {
      const ta = new Date(a.timestamp as string).getTime();
      const tb = new Date(b.timestamp as string).getTime();
      return tb - ta; // desc
    });
    return [...withTs, ...withoutTs];
  }, [job?.admin_notes_list, notesOverride, job?.admin_notes]);

  const { register, handleSubmit, setValue, watch, formState: { errors }, reset } = useForm<JobFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      pallet_type: 'no_preset',
      glass_weight_kg: '',
      small_van: false,
    },
  });

  useEffect(() => {
    if (job) {
      reset({
        source_type: job.source_type as JobSource,
        total_items: job.total_items,
        customer_first_name: job.customer_first_name,
        customer_last_name: job.customer_last_name,
        customer_email: job.customer_email || undefined,
        customer_mobile_number: job.customer_mobile_number || undefined,
        delivery_address_line1: job.delivery_address_line1,
        delivery_address_line2: job.delivery_address_line2 || undefined,
        delivery_city: job.delivery_city || undefined,
        delivery_postcode: job.delivery_postcode,
        delivery_notes: job.delivery_notes || undefined,
        invoice_address_line1: job.invoice_address_line1,
        invoice_address_line2: job.invoice_address_line2 || undefined,
        invoice_city: job.invoice_city || undefined,
        invoice_postcode: job.invoice_postcode,
        total_amount: typeof job.total_amount === 'string' ? job.total_amount : (job.total_amount as number).toFixed(2),
        status: job.status as JobStatus,
        priority: job.priority as JobPriority,
        fulfillment_type: job.fulfillment_type as JobFulfillment,
        job_reference: job.job_reference || undefined,
        website_job_number: job.website_job_number || undefined,
        website_order_created_at: job.website_order_created_at || undefined,
        admin_notes: job.admin_notes || undefined,
        pallet_type: (job.pallet_type as JobPalletType | undefined) ?? 'no_preset',
        glass_weight_kg: formatGlassWeightKgForInput(job.glass_weight_kg) || '',
        small_van: job.small_van ?? false,
      });
      setQuickStatus(job.status as JobStatus);
      setQuickPriority(job.priority as JobPriority);
      setQuickFulfillment(job.fulfillment_type as JobFulfillment);
      setSelectedDeliveryUser(job.delivery_user ? String(job.delivery_user) : '');
      setNotesOverride(null);
    }
  }, [job, reset]);

  const onToggleCopy = () => {
    const invoice1 = watch('invoice_address_line1') || '';
    const invoice2 = watch('invoice_address_line2') || '';
    const invoiceCity = watch('invoice_city') || '';
    const invoicePc = watch('invoice_postcode') || '';
    setCopyInvoiceToDelivery(prev => !prev);
    if (!copyInvoiceToDelivery) {
      setValue('delivery_address_line1', invoice1);
      setValue('delivery_address_line2', invoice2 as any);
      setValue('delivery_city', invoiceCity as any);
      setValue('delivery_postcode', invoicePc);
    }
  };

  const onSubmit = async (values: JobFormData) => {
    try {
      if (!id) return;
      const weightParsed = parseGlassWeightKgInput(String(values.glass_weight_kg ?? ''));
      const payload: Partial<JobFormData> = {
        ...values,
        glass_weight_kg: weightParsed,
        total_amount: typeof values.total_amount === 'number'
          ? values.total_amount.toFixed(2)
          : Number(parseFloat(values.total_amount as unknown as string).toFixed(2)).toString(),
      };
      if (values.fulfillment_type === 'delivery') {
        delete payload.delivery_notes;
      }
      await patchJob({ id, data: payload }).unwrap();
      toast.success('Job Updated', 'The job has been updated successfully.');
      navigate(`/jobs/${id}`);
    } catch (e: any) {
      if (e?.status === 403) {
        toast.warning('Permission Required', "You don't have permission to edit jobs.");
      } else {
        const message = e?.data?.detail || 'Failed to update job. Please review your inputs and try again.';
        toast.error('Update Failed', message);
      }
    }
  };

  const onAssign = async () => {
    if (!id) return;
    if (!selectedDeliveryUser) {
      toast.info('Select a User', 'Please choose a delivery user first.');
      return;
    }
    try {
      const updated = await assignDelivery({ id, data: { delivery_user: Number(selectedDeliveryUser) } }).unwrap();
      toast.success('Assigned', 'Delivery user has been assigned.');
      setSelectedDeliveryUser(updated.delivery_user ? String(updated.delivery_user) : '');
      refetch();
    } catch (e: any) {
      if (e?.status === 403) toast.warning('Permission Required', "You don't have permission to assign delivery.");
      else toast.error('Assignment Failed', e?.data?.detail || 'Could not assign delivery user');
    }
  };

  const onQuickUpdate = async () => {
    if (!id) return;
    try {
      const updated = await patchJob({ id, data: { status: quickStatus, priority: quickPriority, fulfillment_type: quickFulfillment } }).unwrap();
      setQuickStatus(updated.status as JobStatus);
      setQuickPriority(updated.priority as JobPriority);
      setQuickFulfillment(updated.fulfillment_type as JobFulfillment);
      toast.success('Updated', 'Status, priority, or fulfillment updated.');
      refetch();
    } catch (e: any) {
      if (e?.status === 403) toast.warning('Permission Required', "You don't have permission to update these fields.");
      else toast.error('Update Failed', e?.data?.detail || 'Could not update');
    }
  };

  const onQuickNote = async () => {
    if (!id) return;
    if (!quickNote.trim()) {
      toast.info('Note Required', 'Please enter a note to add.');
      return;
    }
    try {
      const updated = await addNote({ id, data: { admin_notes: quickNote } }).unwrap();
      toast.success('Note Added', 'Your note has been added to the job.');
      setQuickNote('');
      setNotesOverride(updated.admin_notes || null);
      refetch();
    } catch (e: any) {
      if (e?.status === 403) toast.warning('Permission Required', "You don't have permission to add notes.");
      else toast.error('Add Note Failed', e?.data?.detail || 'Could not add note');
    }
  };

  useEffect(() => {
    if (showSkipProductionDialog && !prevSkipDialogOpen.current && job) {
      setSkipModalDates({
        glass_arrival_date: job.glass_arrival_date || '',
        manufacture_date: job.manufacture_date || '',
        potential_delivery_date: job.potential_delivery_date || '',
      });
    }
    prevSkipDialogOpen.current = showSkipProductionDialog;
  }, [showSkipProductionDialog, job]);

  const onSkipProductionConfirm = async () => {
    if (!id || !job) return;

    const hideGlassArrival = job.job_type === 'gc';
    const validationError = validateAllJobDates(
      {
        glass_arrival_date: skipModalDates.glass_arrival_date,
        manufacture_date: skipModalDates.manufacture_date,
        potential_delivery_date: skipModalDates.potential_delivery_date,
      },
      { hideGlassArrival, assignedDeliveryUserRole }
    );
    if (validationError) {
      toast.error('Date validation error', validationError);
      return;
    }

    const patch: Record<string, string | null> = {};
    if (!hideGlassArrival && skipDateFieldChanged(job.glass_arrival_date, skipModalDates.glass_arrival_date)) {
      patch.glass_arrival_date = skipModalDates.glass_arrival_date || null;
    }
    if (skipDateFieldChanged(job.manufacture_date, skipModalDates.manufacture_date)) {
      patch.manufacture_date = skipModalDates.manufacture_date || null;
    }
    if (skipDateFieldChanged(job.potential_delivery_date, skipModalDates.potential_delivery_date)) {
      patch.potential_delivery_date = skipModalDates.potential_delivery_date || null;
    }

    try {
      if (Object.keys(patch).length > 0) {
        setIsSkipDatePatching(true);
        try {
          await patchJob({ id, data: patch as Partial<JobFormData> }).unwrap();
        } finally {
          setIsSkipDatePatching(false);
        }
      }
      await completeProduction({ jobId: id, completion_source: 'job_edit_skip' }).unwrap();
      setShowSkipProductionDialog(false);
      toast.success('Production complete', 'Job marked as production complete.');
      await refetch();
    } catch (e: any) {
      const detail = e?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : detail && typeof detail === 'object'
            ? JSON.stringify(detail)
            : e?.data?.lock_reason || 'Please try again';
      toast.error('Failed to complete', msg);
    }
  };

  const onDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete (soft-delete) this job?')) return;
    try {
      await deleteJob(id).unwrap();
      toast.success('Job Deleted', 'The job was soft-deleted.');
      navigate('/jobs');
    } catch (e: any) {
      if (e?.status === 403) toast.warning('Permission Required', "You don't have permission to delete jobs.");
      else toast.error('Delete Failed', e?.data?.detail || 'Could not delete job');
    }
  };

  const onRemakeConfirm = async () => {
    if (!id) return;
    try {
      const newJob = await remakeJob({ id }).unwrap();
      setShowRemakeModal(false);
      toast.success('Remake created', 'Opening the new job edit page.');
      navigate(`/jobs/${newJob.id}/edit`);
    } catch (e: any) {
      if (e?.status === 403) {
        toast.warning('Permission Required', "You don't have permission to remake jobs.");
      } else {
        toast.error('Remake failed', e?.data?.detail || 'Please try again.');
      }
    }
  };

  // Tabs state (declare BEFORE any early returns to keep hooks order stable)
  const isOrdersRouteFlag = location.pathname.endsWith('/orders') || location.hash === '#orders';
  const [activeTab, setActiveTab] = useState<'overview' | 'orders' | 'delivery' | 'collection'>(
    isOrdersRouteFlag ? 'orders' : 'overview'
  );
  useEffect(() => { if (isOrdersRouteFlag) setActiveTab('orders'); }, [isOrdersRouteFlag]);

  if (isLoading) {
    return (
      <AppShell>
        <PageLayout
          title="Edit Job"
          subtitle="Loading job..."
          bgColor={PAGE_COLORS.jobs.bgColor}
          textColor={PAGE_COLORS.jobs.textColor}
          borderColor={PAGE_COLORS.jobs.borderColor}
          icon={<ClipboardList className="w-6 h-6" />}
        >
          <div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
        </PageLayout>
      </AppShell>
    );
  }

  if (isError || !job) {
    return (
      <AppShell>
        <PageLayout
          title="Edit Job"
          subtitle="Unable to load job"
          bgColor={PAGE_COLORS.jobs.bgColor}
          textColor={PAGE_COLORS.jobs.textColor}
          borderColor={PAGE_COLORS.jobs.borderColor}
          icon={<ClipboardList className="w-6 h-6" />}
        >
          <div className="mb-4">
            <Link to="/jobs"><Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" />}>Back to Jobs</Button></Link>
          </div>
          <ContentCard title="Error">
            <div className="text-gray-700">Please try again later.</div>
          </ContentCard>
        </PageLayout>
      </AppShell>
    );
  }

  const fulfillmentStatusInput = {
    status: job.status,
    production_status: job.production_status,
    fulfillment_type: job.fulfillment_type,
    delivery_result:
      job.fulfillment_type === 'delivery'
        ? (deliveryData?.delivery_result ?? job.delivery_result ?? 'pending')
        : undefined,
    collection_completed: job.collection_completed,
  };
  const fulfillmentBadge = getJobStatusBadge(fulfillmentStatusInput);
  const fulfillmentLabel = getAdminJobStatusLabel(fulfillmentStatusInput);
  const fulfillmentPillClass = getAdminJobStatusPillClassNames(fulfillmentStatusInput);

  return (
    <AppShell>
      <PageLayout
        title="Edit Job"
        subtitle={`Editing ${job.customer_first_name} ${job.customer_last_name} • ${job.website_job_number || '-'}`}
        bgColor={PAGE_COLORS.jobs.bgColor}
        textColor={PAGE_COLORS.jobs.textColor}
        borderColor={PAGE_COLORS.jobs.borderColor}
        icon={<ClipboardList className="w-6 h-6" />}
      >
        {/* Tab Header */}
        <div className="mb-4 flex items-center justify-between">
          <Link to={`/jobs/${id}`}>
            <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" />}>Back to Details</Button>
          </Link>
          <Button type="button" variant="ghost" size="sm" className="text-red-600" icon={<Trash2 className="w-4 h-4" />} onClick={onDelete} disabled={isDeleting}>
            Delete
          </Button>
        </div>
        <div className="mb-3 border-b border-gray-200">
          <nav className="-mb-px flex gap-4" aria-label="Tabs">
            <button className={`whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium ${activeTab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'}`} onClick={() => setActiveTab('overview')}>Overview</button>
            <button className={`whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium ${activeTab === 'orders' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'}`} onClick={() => setActiveTab('orders')}>Orders</button>
            <button className={`whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium ${activeTab === 'delivery' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'}`} onClick={() => setActiveTab('delivery')}>Delivery</button>
            {job.fulfillment_type === 'collection' && (
              <button
                className={`whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium ${activeTab === 'collection' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'}`}
                onClick={() => setActiveTab('collection')}
              >
                Collection
              </button>
            )}
          </nav>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600">Fulfillment status:</span>
          <span
            className="px-1.5 py-0.5 rounded text-xs font-bold text-white"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
          >
            {fulfillmentBadge}
          </span>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${fulfillmentPillClass}`}
          >
            {fulfillmentLabel}
          </span>
        </div>

        {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-4">
            {/* Job Details */}
            <ContentCard title="Job Details">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Source</label>
                  <select className="form-input" {...register('source_type')}>
                    <option value="manual">Manual</option>
                    <option value="website">Website</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Website Job #</label>
                  <input className="form-input" placeholder="#10001" {...register('website_job_number')} />
                </div>
                <div>
                  <label className="form-label">Job Reference (internal)</label>
                  <input className="form-input" {...register('job_reference')} />
                </div>
              </div>
            </ContentCard>

            {/* Customer Details */}
            <ContentCard title="Customer Details">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">First Name</label>
                  <input className="form-input" {...register('customer_first_name')} />
                  {errors.customer_first_name && <p className="form-error">{errors.customer_first_name.message as string}</p>}
                </div>
                <div>
                  <label className="form-label">Last Name</label>
                  <input className="form-input" {...register('customer_last_name')} />
                  {errors.customer_last_name && <p className="form-error">{errors.customer_last_name.message as string}</p>}
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <input className="form-input" placeholder="customer@example.com" {...register('customer_email')} />
                </div>
                <div>
                  <label className="form-label">Mobile</label>
                  <input className="form-input" placeholder="+44..." {...register('customer_mobile_number')} />
                </div>
              </div>
            </ContentCard>

            {/* Invoice Address */}
            <ContentCard title="Invoice Address">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Address Line 1</label>
                  <input className="form-input" {...register('invoice_address_line1')} />
                  {errors.invoice_address_line1 && <p className="form-error">{errors.invoice_address_line1.message as string}</p>}
                </div>
                <div>
                  <label className="form-label">Address Line 2</label>
                  <input className="form-input" {...register('invoice_address_line2')} />
                </div>
                <div>
                  <label className="form-label">City</label>
                  <input className="form-input" {...register('invoice_city')} />
                </div>
                <div>
                  <label className="form-label">Postcode</label>
                  <input className="form-input" {...register('invoice_postcode')} />
                  {errors.invoice_postcode && <p className="form-error">{errors.invoice_postcode.message as string}</p>}
                </div>
              </div>
            </ContentCard>

            {/* Delivery Address */}
            <ContentCard title="Delivery Address" subtitle="You can copy from invoice address">
              <div className="flex items-center justify-between mb-3">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" className="form-checkbox" checked={copyInvoiceToDelivery} onChange={onToggleCopy} />
                  Same as invoice address
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Address Line 1</label>
                  <input className="form-input" {...register('delivery_address_line1')} />
                  {errors.delivery_address_line1 && <p className="form-error">{errors.delivery_address_line1.message as string}</p>}
                </div>
                <div>
                  <label className="form-label">Address Line 2</label>
                  <input className="form-input" {...register('delivery_address_line2')} />
                </div>
                <div>
                  <label className="form-label">City</label>
                  <input className="form-input" {...register('delivery_city')} />
                </div>
                <div>
                  <label className="form-label">Postcode</label>
                  <input className="form-input" {...register('delivery_postcode')} />
                  {errors.delivery_postcode && <p className="form-error">{errors.delivery_postcode.message as string}</p>}
                </div>
                {job.fulfillment_type !== 'delivery' && (
                  <div className="md:col-span-3">
                    <label className="form-label">Notes</label>
                    <textarea className="form-input" rows={3} {...register('delivery_notes')} />
                  </div>
                )}
              </div>
              {job.fulfillment_type === 'delivery' && (
                <p className="mt-2 text-xs text-gray-500">
                  Delivery notes are managed on the Delivery tab under Delivery Window.
                </p>
              )}
            </ContentCard>

            {/* Order Summary */}
            <ContentCard title="Order Summary">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Total Items</label>
                  <input type="number" min={0} className="form-input" {...register('total_items', { valueAsNumber: true })} />
                  {errors.total_items && <p className="form-error">{errors.total_items.message as string}</p>}
                </div>
                <div>
                  <label className="form-label">Total Amount</label>
                  <input className="form-input" placeholder="111.23" {...register('total_amount')} />
                  {errors.total_amount && <p className="form-error">{errors.total_amount.message as string}</p>}
                </div>
                {isPalletAssign && (
                  <div className="md:col-span-2 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2 text-sm text-gray-700">
                    <span className="font-medium text-gray-800">Glass weight (preview):</span>{' '}
                    {formatGlassWeightKgPreviewKg(
                      parseGlassWeightKgInput(String(watch('glass_weight_kg') ?? ''))
                    )}
                  </div>
                )}
              </div>
            </ContentCard>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button type="submit" onClick={handleSubmit(onSubmit)} disabled={isSaving}>Save Changes</Button>
              <Link to={`/jobs/${id}`}><Button variant="ghost">Cancel</Button></Link>
            </div>
          </div>

          {/* Right column: Quick Actions & Job Settings */}
          <div className="lg:col-span-1 space-y-4">
            <ContentCard title="Assign Delivery">
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 items-center">
                  <select className="form-input flex-1 bg-gradient-to-r from-blue-50 to-green-50 border-blue-200 focus:border-blue-400 focus:ring-blue-400" value={selectedDeliveryUser} onChange={(e) => setSelectedDeliveryUser(e.target.value)}>
                    <option value="">
                      {deliveryOptions.length > 0 ? 'Select delivery user…' : 'No Collection & Delivery users available'}
                    </option>
                    {deliveryOptions.map(opt => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label} • [{opt.role}]
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-2">
                    <Button type="button" onClick={onAssign} disabled={isAssigning}>Assign</Button>
                  </div>
                </div>
                {isPalletAssign && (
                  <div className="mt-3 space-y-3 rounded-lg border border-blue-100 bg-white/80 p-3">
                    <p className="text-xs font-medium text-gray-600">Pallet delivery details</p>
                    <div>
                      <label className="form-label">Pallet Type</label>
                      <select className="form-input w-full" {...register('pallet_type')}>
                        {PALLET_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {errors.pallet_type && (
                        <p className="form-error">{errors.pallet_type.message as string}</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label">Glass Weight</label>
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder="e.g. 5.67"
                          className="form-input max-w-[12rem] flex-1 min-w-[8rem]"
                          {...register('glass_weight_kg')}
                        />
                        <span className="text-sm text-gray-500">kg (saved with order)</span>
                      </div>
                      {errors.glass_weight_kg && (
                        <p className="form-error">{errors.glass_weight_kg.message as string}</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label">Small van</label>
                      <select
                        className="form-input w-full"
                        value={watch('small_van') ? 'yes' : 'no'}
                        onChange={(e) => setValue('small_van', e.target.value === 'yes')}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </ContentCard>

            <JobProductionDatesCard
              glass_arrival_date={job.glass_arrival_date}
              manufacture_date={job.manufacture_date}
              potential_delivery_date={job.potential_delivery_date}
              job_type={job.job_type}
            />

            <ContentCard title="Remake Job">
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Copy className="h-4 w-4 text-gray-500 shrink-0 mt-0.5" aria-hidden />
                  <p className="text-sm text-gray-600">
                    Create a fresh manual remake of this job: customer and order specs and uploaded order files are
                    copied; production and delivery workflow state is reset. The delivery postcode will have{' '}
                    <span className="font-medium text-gray-800">&quot; - REMAKE&quot;</span> appended. The current job is
                    not changed.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowRemakeModal(true)}
                  disabled={isRemaking}
                  className="w-full sm:w-auto"
                >
                  Remake Job
                </Button>
              </div>
            </ContentCard>

            <ContentCard title="Admin Notes">
              <div className="flex flex-col gap-3">
                <div className="flex gap-2 items-start">
                  <textarea className="form-input flex-1" rows={3} placeholder="Write a note..." value={quickNote} onChange={(e) => setQuickNote(e.target.value)} />
                  <div className="flex items-center gap-2">
                    <Button type="button" onClick={onQuickNote} disabled={isAddingNote}>Add</Button>
                  </div>
                </div>
                <div className="text-xs text-gray-600">
                  {job.updated_by_username ? (
                    <>Last updated by <span className="font-medium">{job.updated_by_username}</span> at {new Date(job.updated_at).toLocaleString()}</>
                  ) : (
                    <>Last updated at {new Date(job.updated_at).toLocaleString()}</>
                  )}
                </div>
                {parsedNotes.length > 0 ? (
                  <ul className="space-y-2">
                    {parsedNotes.map((n, idx) => (
                      <li key={idx} className="bg-gray-50 border border-gray-200 rounded-md p-3">
                        <div className="text-xs text-gray-600 mb-1">
                          {n.timestamp && n.author ? (
                            <>
                              {n.timestamp} • {n.author}
                            </>
                          ) : (
                            <>Legacy note (author unknown)</>
                          )}
                        </div>
                        <div className="text-sm text-gray-800 whitespace-pre-wrap">{n.text}</div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-gray-500">No notes yet.</div>
                )}
              </div>
            </ContentCard>

            {/* Lock/Unlock Job Section */}
            <ContentCard title="Lock Job">
              <JobLockManager
                jobId={id!}
                currentLock={job?.lock_data || null}
                onLockUpdate={(lockData) => {
                  console.log('Lock status updated:', lockData);
                }}
              />
            </ContentCard>

            {job?.status === 'production' && (
              <ContentCard title="Skip Production">
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Skip the rest of the production workflow and mark this job production complete—the same action as
                    &quot;Complete production&quot; on Production Line (dashboard).
                  </p>
                  <Button
                    type="button"
                    onClick={() => setShowSkipProductionDialog(true)}
                    disabled={isCompletingProduction}
                    className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto"
                  >
                    Skip production…
                  </Button>
                </div>
              </ContentCard>
            )}

            {isJobProductionComplete && jobEditSkipCompletion && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-4 shadow-sm">
                  <p className="text-sm font-medium text-amber-900">Production skipped (edit job)</p>
                  <p className="mt-1 text-sm text-amber-950/90">
                    Production was skipped by{' '}
                    <span className="font-semibold text-amber-950">
                      {jobEditSkipCompletion.moved_by_username || 'Unknown user'}
                    </span>
                    {jobEditSkipCompletion.moved_at ? (
                      <>
                        {' '}
                        at{' '}
                        <time dateTime={jobEditSkipCompletion.moved_at} className="font-medium text-amber-950">
                          {new Date(jobEditSkipCompletion.moved_at).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })}
                        </time>
                      </>
                    ) : null}
                    .
                  </p>
                  {(() => {
                    const d = jobEditSkipCompletion.dates_at_completion;
                    if (!d) return null;
                    const rows: { label: string; value: string }[] = [];
                    if (d.glass_arrival_date) {
                      rows.push({
                        label: 'Glass arrival',
                        value: formatJobDateLabel(d.glass_arrival_date, false),
                      });
                    }
                    if (d.manufacture_date) {
                      rows.push({
                        label: 'Manufacturing',
                        value: formatJobDateLabel(d.manufacture_date, false),
                      });
                    }
                    if (d.potential_delivery_date) {
                      rows.push({
                        label: 'Potential delivery',
                        value: formatJobDateLabel(d.potential_delivery_date, true),
                      });
                    }
                    if (rows.length === 0) return null;
                    return (
                      <ul className="mt-3 space-y-1 border-t border-amber-200/80 pt-3 text-sm text-amber-950/90">
                        {rows.map((r) => (
                          <li key={r.label}>
                            <span className="text-amber-800/90">{r.label}:</span>{' '}
                            <span className="font-medium">{r.value}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
              )}

            {/* Move Back to Production Section */}
            {isJobProductionComplete && (
              <ContentCard title="Move Back to Production">
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    This job has completed production. You can move it back to the production workflow if needed.
                  </p>
                  <MoveBackToProductionButton
                    jobId={id!}
                    jobNumber={job.website_job_number || id!}
                    onSuccess={() => {
                      // Refetch job data to show updated status
                      window.location.reload();
                    }}
                  />
                </div>
              </ContentCard>
            )}

            <ContentCard title="Quick Updates">
              <div className="space-y-3">
                <div className="flex gap-2 items-center">
                  <label className="w-24 text-sm text-gray-600">Status</label>
                  <select className="form-input flex-1" value={quickStatus} onChange={(e) => setQuickStatus(e.target.value as JobStatus)}>
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="production">Production</option>
                  </select>
                </div>
                <div className="flex gap-2 items-center">
                  <label className="w-24 text-sm text-gray-600">Priority</label>
                  <select className="form-input flex-1" value={quickPriority} onChange={(e) => setQuickPriority(e.target.value as JobPriority)}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div className="flex gap-2 items-center">
                  <label className="w-24 text-sm text-gray-600">Fulfillment</label>
                  <select className="form-input flex-1" value={quickFulfillment} onChange={(e) => setQuickFulfillment(e.target.value as JobFulfillment)}>
                    <option value="delivery">Delivery</option>
                    <option value="collection">Collection</option>
                    <option value="pallet">Pallet</option>
                  </select>
                </div>
                <div className="flex justify-end">
                  <div className="flex items-center gap-2">
                    <Button type="button" onClick={onQuickUpdate} disabled={isSaving}>Update</Button>
                  </div>
                </div>
              </div>
            </ContentCard>
          </div>
        </div>
        )}

        {activeTab === 'orders' && (
          <div className="mt-2">
            <OrdersEditPanel jobId={id!} />
          </div>
        )}

        {activeTab === 'delivery' && (
          <div className="mt-2 space-y-4">
            {job.fulfillment_type === 'delivery' && (
              <ContentCard title="Delivery Window">
                <DeliveryDetailsForm
                  jobId={id!}
                  initialData={{
                    delivery_window_start: job.delivery_window_start,
                    delivery_window_end: job.delivery_window_end,
                    delivery_status: job.delivery_status,
                    delivery_notes: job.delivery_notes,
                  }}
                  deliveryScheduleEmailSentAt={job.delivery_schedule_email_sent_at}
                  deliveryScheduleEmailSentByUsername={job.delivery_schedule_email_sent_by_username}
                  onSave={() => refetch()}
                  onEmailSent={() => refetch()}
                />
              </ContentCard>
            )}
            <DeliveryWorkflow
              jobId={id!}
              deliveryResult={deliveryData?.delivery_result || 'pending'}
              deliveryRecord={deliveryData?.record || null}
              onStatusChange={refetchDelivery}
            />
          </div>
        )}

        {activeTab === 'collection' && job.fulfillment_type === 'collection' && (
          <div className="mt-2">
            <CollectionTab jobId={id!} lockData={job.lock_data} />
          </div>
        )}
      </PageLayout>

      {showRemakeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="remake-job-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4">
            <h2 id="remake-job-title" className="text-lg font-semibold text-gray-900">
              Create remake?
            </h2>
            <p className="text-sm text-gray-600">
              This creates a new manual job with copied orders and uploaded order assets, resets workflow state, and
              appends <span className="font-medium">&quot; - REMAKE&quot;</span> to the delivery postcode. The original
              job stays as-is.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setShowRemakeModal(false)} disabled={isRemaking}>
                Cancel
              </Button>
              <Button type="button" onClick={onRemakeConfirm} disabled={isRemaking}>
                {isRemaking ? 'Creating…' : 'Confirm remake'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showSkipProductionDialog && job?.status === 'production' && (
        <CompleteJobConfirmDialog
          isOpen={showSkipProductionDialog}
          jobNumber={job.website_job_number || id!}
          title="Skip production?"
          panelClassName="max-w-lg"
          leadContent={
            <>
              This will mark job{' '}
              <strong className="font-bold text-gray-900">{job.website_job_number || id}</strong> production complete and
              skip remaining production steps—the same outcome as completing from Production Line (dashboard).
            </>
          }
          cancelLabel="Cancel"
          confirmLabel="Confirm"
          confirmingLabel="Completing…"
          onConfirm={onSkipProductionConfirm}
          onCancel={() => setShowSkipProductionDialog(false)}
          isLoading={isCompletingProduction || isSkipDatePatching}
          extraContent={
            <SkipProductionDateFields
              jobId={id!}
              lockData={job.lock_data || null}
              hideGlassArrival={job.job_type === 'gc'}
              dates={skipModalDates}
              onChange={(field, value) =>
                setSkipModalDates((prev) => ({ ...prev, [field]: value }))
              }
            />
          }
        />
      )}
    </AppShell>
  );
};
